<template>
    <div class="contents">
        <div class="container-fluid">
            <div class="row">
                <div class="col-lg-12">
                    <div class="breadcrumb-main user-member justify-content-sm-between">
                        <h4 class="text-capitalize fw-500 breadcrumb-title">
                            <router-link to="/admin/support-tickets"><i class="fas fa-arrow-left"></i></router-link>
                            &nbsp; Tiket #{{ ticket ? ticket.ticket_number : '...' }}
                        </h4>
                        <div v-if="ticket" class="action-btn">
                            <select v-model="ticket.status" @change="updateStatus" class="form-control form-control-sm d-inline-block mr-2" style="width:auto;">
                                <option value="open">Open</option>
                                <option value="in_progress">Diproses</option>
                                <option value="waiting_user">Menunggu User</option>
                                <option value="closed">Closed</option>
                                <option value="refund">Refund</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            <div v-if="loading" class="text-center p-5"><div class="spinner-border"></div></div>
            <div v-else-if="!ticket" class="alert alert-danger">Tiket tidak ditemukan</div>
            <div v-else class="row">
                <div class="col-lg-8">
                    <div class="card p-3 mb-3">
                        <h5>{{ ticket.subject }}</h5>
                        <div class="text-muted small">
                            <span><i class="fas fa-folder"></i> {{ categoryLabel(ticket.category) }}</span>
                            <span class="ml-3"><i class="fas fa-flag"></i> {{ priorityLabel(ticket.priority) }}</span>
                            <span class="ml-3"><i class="fas fa-clock"></i> {{ formatDate(ticket.created_at) }}</span>
                        </div>
                    </div>

                    <div class="card p-3 mb-3 thread-card">
                        <div v-for="m in messages" :key="m.id" :class="['message-row', 'sender-' + m.sender_type]">
                            <div class="message-bubble" :class="'bubble-' + m.sender_type">
                                <div class="message-header">
                                    <strong>{{ senderLabel(m.sender_type) }}</strong>
                                    <small class="text-muted ml-2">{{ formatDate(m.created_at) }}</small>
                                </div>
                                <div class="message-body" v-html="formatMessage(m.message)"></div>
                                <div v-if="parseAttachments(m.attachments).length > 0" class="message-attachments">
                                    <div v-for="(a, i) in parseAttachments(m.attachments)" :key="i" class="attachment-item">
                                        <img v-if="isImage(a.mime)" :src="a.url" :alt="a.filename" class="attach-image" @click="openLightbox(a.url)">
                                        <video v-else-if="isVideo(a.mime)" controls :src="a.url" class="attach-video"></video>
                                        <a v-else :href="a.url" target="_blank" class="attach-file">
                                            <i :class="fileIcon(a.mime)"></i>
                                            {{ a.filename }} <small>({{ formatSize(a.size) }})</small>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="ticket.status !== 'closed'" class="card p-3 mb-3" style="border:1px solid #c7d2fe;">
                        <div class="d-flex justify-content-between align-items-center">
                            <h6 class="mb-0"><i class="fas fa-file-invoice-dollar text-success"></i> Generate Invoice</h6>
                            <button @click="toggleInvoice" class="btn btn-sm btn-outline-success">
                                <i class="fas" :class="showInvoice ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                                {{ showInvoice ? 'Tutup' : 'Buka' }}
                            </button>
                        </div>
                        <div v-if="showInvoice" class="mt-3">
                            <div v-if="invoiceLoading" class="text-center p-3"><div class="spinner-border spinner-border-sm"></div> Memuat paket...</div>
                            <div v-else>
                                <div class="form-group mb-2">
                                    <label class="small font-weight-bold mb-1">Pilih Paket <span class="text-muted">(bisa ganti paket)</span></label>
                                    <select v-model.number="invoicePackageId" @change="generateCopywriting" class="form-control">
                                        <optgroup label="Paket Utama">
                                            <option v-for="p in invoicePackagesMain" :key="p.id" :value="p.id">{{ p.name }} — {{ formatSaldo(p.price) }}{{ p.duration ? ' (' + p.duration + ' hari)' : '' }}</option>
                                        </optgroup>
                                        <optgroup label="Add-On">
                                            <option v-for="p in invoicePackagesAddon" :key="p.id" :value="p.id">{{ p.name }} — {{ formatSaldo(p.price) }}</option>
                                        </optgroup>
                                    </select>
                                    <small v-if="invoicePackageId === invoiceCurrentPackageId" class="text-success"><i class="fas fa-check-circle"></i> Paket aktif user (perpanjangan)</small>
                                    <small v-else class="text-warning"><i class="fas fa-exchange-alt"></i> Berbeda dari paket aktif user (ganti paket)</small>
                                    <small v-if="invoiceUser" class="d-block text-muted">Saldo user: {{ formatSaldo((invoiceUser && invoiceUser.balance) || 0) }}</small>
                                </div>
                                <div class="form-group mb-2">
                                    <label class="small font-weight-bold mb-1">Status Invoice</label>
                                    <div class="btn-group btn-group-sm btn-block" role="group">
                                        <button type="button" class="btn" :class="invoiceMarkPaid ? 'btn-outline-secondary' : 'btn-warning'" @click="invoiceMarkPaid = false">
                                            <i class="fas fa-clock"></i> Unpaid (Menunggu Pembayaran)
                                        </button>
                                        <button type="button" class="btn" :class="invoiceMarkPaid ? 'btn-success' : 'btn-outline-secondary'" @click="invoiceMarkPaid = true">
                                            <i class="fas fa-check-circle"></i> Paid (Lunas)
                                        </button>
                                    </div>
                                    <small class="text-muted d-block mt-1">
                                        <span v-if="invoiceMarkPaid"><i class="fas fa-info-circle text-success"></i> PDF akan dicetak dengan watermark <strong>"PAID"</strong> sebagai bukti pembayaran sukses</span>
                                        <span v-else><i class="fas fa-info-circle"></i> PDF default — invoice tagihan untuk user bayar</span>
                                    </small>
                                </div>
                                <div class="form-group mb-2">
                                    <label class="small font-weight-bold mb-1">Copywriting <span class="text-muted">(bisa diedit sebelum kirim)</span></label>
                                    <textarea v-model="invoiceText" class="form-control" rows="11" style="font-size:13px;"></textarea>
                                </div>
                                <div class="d-flex justify-content-between align-items-center">
                                    <small class="text-muted"><i class="fas fa-paperclip"></i> PDF invoice otomatis dilampirkan</small>
                                    <button @click="sendInvoice" :disabled="invoiceSending || !invoicePackageId || !invoiceText.trim()" class="btn" :class="invoiceMarkPaid ? 'btn-success' : 'btn-primary'">
                                        <span v-if="invoiceSending"><i class="fas fa-spinner fa-spin"></i> Mengirim...</span>
                                        <span v-else><i class="fas fa-paper-plane"></i> Kirim {{ invoiceMarkPaid ? 'Bukti PAID' : 'Invoice' }} ke Tiket</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="ticket.status !== 'closed'" class="card p-3">
                        <div class="d-flex justify-content-between align-items-center mb-2">
                            <h6 class="mb-0"><i class="fas fa-reply"></i> Balas sebagai Tim Support</h6>
                            <button type="button" @click="startAIDraft" :disabled="aiDraftProcessing || aiDraftReady || aiDraftStarting" class="btn btn-sm btn-outline-secondary">
                                <span v-if="aiDraftProcessing"><i class="fas fa-spinner fa-spin"></i> AI Draft…</span>
                                <span v-else><i class="fas fa-robot"></i> AI Draft</span>
                            </button>
                        </div>
                        <div v-if="aiDraftProcessing" class="alert alert-info py-2 small mb-2">
                            <i class="fas fa-search"></i> AI sedang investigasi tiket (biasanya 1–3 menit)…
                        </div>
                        <div v-if="aiDraftReady" class="mb-2 p-2" style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:6px;">
                            <div class="d-flex justify-content-between align-items-center">
                                <strong class="small text-success"><i class="fas fa-robot"></i> Draft AI siap</strong>
                                <button type="button" @click="showAiAdminSummary = !showAiAdminSummary" class="btn btn-link btn-sm p-0">
                                    {{ showAiAdminSummary ? 'Sembunyikan ringkasan' : 'Ringkasan internal' }}
                                </button>
                            </div>
                            <div v-if="showAiAdminSummary && aiDraftAdminSummary" class="small text-muted mt-2" style="white-space:pre-wrap;">{{ aiDraftAdminSummary }}</div>
                            <div v-if="aiDraftNeedsEngineering" class="mt-2">
                                <button type="button" @click="showAiEngineering = !showAiEngineering" class="btn btn-sm btn-outline-warning">
                                    <i class="fas fa-wrench"></i> {{ showAiEngineering ? 'Tutup prompt engineering' : 'Lihat prompt engineering' }}
                                </button>
                                <pre v-if="showAiEngineering && aiDraftEngineeringPrompt" class="small mt-2 mb-0 p-2" style="max-height:200px;overflow:auto;background:#fffbeb;">{{ aiDraftEngineeringPrompt }}</pre>
                            </div>
                            <div class="d-flex flex-wrap mt-2">
                                <button type="button" @click="sendAIDraft" :disabled="aiDraftSending || !replyText.trim()" class="btn btn-sm btn-success mr-2 mb-1">
                                    <span v-if="aiDraftSending"><i class="fas fa-spinner fa-spin"></i></span>
                                    <span v-else><i class="fas fa-check"></i> Kirim ke user</span>
                                </button>
                                <button type="button" @click="discardAIDraft" :disabled="aiDraftSending" class="btn btn-sm btn-outline-danger mb-1">
                                    <i class="fas fa-trash"></i> Buang draft
                                </button>
                            </div>
                        </div>
                        <textarea v-model="replyText" class="form-control" rows="5" placeholder="Tulis balasan untuk user..."></textarea>
                        <div class="mt-2">
                            <input ref="replyFiles" type="file" multiple @change="onFileSelect" class="form-control-file" accept="*/*">
                            <div v-if="replyFiles.length > 0" class="mt-2">
                                <div v-for="(f, i) in replyFiles" :key="i" class="d-flex justify-content-between align-items-center small p-1" style="background:#f9fafb; border-radius:4px; margin-bottom:4px;">
                                    <span><i class="fas fa-paperclip"></i> {{ f.name }} ({{ formatSize(f.size) }})</span>
                                    <button @click="removeFile(i)" class="btn btn-sm btn-link text-danger p-0"><i class="fas fa-times"></i></button>
                                </div>
                            </div>
                        </div>
                        <div class="text-right mt-3">
                            <button @click="sendReply" :disabled="sending || !replyText.trim()" class="btn btn-primary">
                                <span v-if="sending"><i class="fas fa-spinner fa-spin"></i> Mengirim...</span>
                                <span v-else><i class="fas fa-paper-plane"></i> Kirim Balasan</span>
                            </button>
                        </div>
                    </div>
                    <div v-else class="alert alert-secondary text-center">
                        <i class="fas fa-lock"></i> Tiket sudah ditutup. Ubah status ke Open/In Progress untuk balas.
                    </div>
                </div>

                <div class="col-lg-4">
                    <div class="card p-3 mb-3">
                        <h6><i class="fas fa-user"></i> Info User</h6>
                        <table class="table table-sm small mb-0">
                            <tbody>
                                <tr><td class="text-muted">Nama</td><td>{{ (user && user.name) || '-' }}</td></tr>
                                <tr><td class="text-muted">Email</td><td>{{ (user && user.email) || '-' }}</td></tr>
                                <tr><td class="text-muted">Phone</td><td>{{ (user && user.phone) || '-' }}</td></tr>
                                <tr><td class="text-muted">Level</td><td>{{ (user && user.level) || '-' }}</td></tr>
                                <tr><td class="text-muted">Saldo</td><td>
                                    <strong>{{ formatSaldo((user && user.balance) || 0) }}</strong>
                                    <small v-if="user && user.withdrawable_balance" class="text-muted d-block">Withdrawable: {{ formatSaldo(user.withdrawable_balance) }}</small>
                                </td></tr>
                                <tr v-if="showAgencyRow"><td class="text-muted">Agency</td><td>
                                    <span v-if="agencyInfo && agencyInfo.found">
                                        <strong>{{ agencyInfo.name || agencyInfo.email }}</strong>
                                        <span v-if="agencyInfo.source === 'v2'" class="badge badge-warning ml-1" style="font-size:9px;" title="Ditemukan via fallback v2 starsender" data-name="agency-source-badge">v2</span>
                                        <span v-else class="badge badge-success ml-1" style="font-size:9px;" title="Ditemukan di v3">v3</span>
                                        <br>
                                        <small class="text-muted">{{ agencyInfo.email }}</small>
                                        <small v-if="agencyInfo.phone" class="text-muted d-block">{{ agencyInfo.phone }}</small>
                                    </span>
                                    <span v-else class="text-warning small">
                                        <i class="fas fa-exclamation-triangle"></i> Tidak terdeteksi
                                        <small v-if="agencyInfo && agencyInfo.note" class="d-block text-muted">{{ agencyInfo.note }}</small>
                                    </span>
                                </td></tr>
                                <tr><td class="text-muted">Paket</td><td>{{ userPackage || '-' }}</td></tr>
                                <tr><td class="text-muted">Expired Paket</td><td :class="expiredClass">{{ formatExpired() }}</td></tr>
                                <tr><td class="text-muted">Status API</td><td>
                                    <span v-if="userApiBlocked == 1" class="badge badge-danger">API DIBLOKIR</span>
                                    <span v-else class="badge badge-success">API Aktif</span>
                                    <div v-if="userApiBlocked == 1 && userApiBlockedReason" class="text-danger" style="font-size:11px;margin-top:3px;">
                                        Alasan: {{ userApiBlockedReason }}
                                    </div>
                                </td></tr>
                                <tr><td class="text-muted">Total Tiket</td><td>{{ userTotalTickets + 1 }}</td></tr>
                                <tr><td class="text-muted">Bergabung</td><td>{{ formatDate((user && user.created_at)) }}</td></tr>
                            </tbody>
                        </table>
                    </div>

                    <div class="card p-3 mb-3" style="border-left: 4px solid #ffc107;">
                        <h6><i class="fas fa-bolt text-warning"></i> Aksi Cepat Admin</h6>
                        <button @click="toggleExtendPackage" class="btn btn-sm btn-outline-primary btn-block mb-2">
                            <i class="fas fa-clock"></i> Perpanjang Paket
                        </button>
                        <div v-if="showExtendPackage" class="mt-2 p-2 mb-2" style="background:#f8f9fa;border-radius:6px;">
                            <label class="small font-weight-bold mb-1 d-block">User: {{ (user && user.email) || '-' }}</label>
                            <label class="small text-muted mb-1 d-block">Paket saat ini: {{ userPackage || '-' }}</label>
                            <label class="small font-weight-bold mb-1">Durasi Perpanjangan</label>
                            <select v-model.number="extendPackageDays" class="form-control form-control-sm mb-2">
                                <option v-for="opt in extendPackageOptions" :key="opt.days" :value="opt.days">{{ opt.label }} ({{ opt.days }} hari)</option>
                            </select>
                            <button @click="adminExtendPackage" :disabled="!extendPackageDays || extendPackageSubmitting" class="btn btn-sm btn-primary btn-block">
                                <span v-if="extendPackageSubmitting"><i class="fas fa-spinner fa-spin"></i></span>
                                <span v-else><i class="fas fa-check"></i> Konfirmasi Perpanjang</span>
                            </button>
                        </div>
                        <button @click="toggleChangePackage" class="btn btn-sm btn-outline-info btn-block mb-2">
                            <i class="fas fa-exchange-alt"></i> Ganti Paket
                        </button>
                        <div v-if="showChangePackage" class="mt-2 p-2" style="background:#f8f9fa;border-radius:6px;">
                            <label class="small font-weight-bold mb-1">Pilih Paket Baru</label>
                            <select v-model.number="changePackageId" class="form-control form-control-sm mb-2">
                                <option :value="0">— Pilih paket —</option>
                                <optgroup label="Paket Utama">
                                    <option v-for="p in invoicePackagesMain" :key="p.id" :value="p.id">{{ p.name }}{{ p.duration ? ' (' + p.duration + ' hari)' : '' }}</option>
                                </optgroup>
                                <optgroup label="Add-On">
                                    <option v-for="p in invoicePackagesAddon" :key="p.id" :value="p.id">{{ p.name }}</option>
                                </optgroup>
                            </select>
                            <label class="small font-weight-bold mb-1">Durasi (hari, kosongkan = pakai default paket)</label>
                            <input type="number" v-model.number="changePackageDays" class="form-control form-control-sm mb-2" placeholder="default: durasi paket" min="0" />
                            <button @click="adminChangePackage" :disabled="!changePackageId || changePackageSubmitting" class="btn btn-sm btn-info btn-block">
                                <span v-if="changePackageSubmitting"><i class="fas fa-spinner fa-spin"></i></span>
                                <span v-else><i class="fas fa-check"></i> Konfirmasi Ganti</span>
                            </button>
                        </div>
                        <button @click="adminResetPassword" class="btn btn-sm btn-outline-warning btn-block">
                            <i class="fas fa-key"></i> Reset Password User
                        </button>
                        <button @click="adminLoginAsUser" class="btn btn-sm btn-primary btn-block mt-2">
                            <i class="fas fa-user-secret"></i> Login sebagai User
                        </button>
                        <button @click="toggleAdjustBalance" class="btn btn-sm btn-outline-success btn-block mt-2">
                            <i class="fas fa-wallet"></i> Tambah / Kurangi Saldo
                        </button>
                        <div v-if="showAdjustBalance" class="mt-2 p-2" style="background:#f8f9fa;border-radius:6px;">
                            <label class="small font-weight-bold mb-1 d-block">Saldo saat ini: {{ formatSaldo((user && user.balance) || 0) }}</label>
                            <select v-model="adjustBalanceType" class="form-control form-control-sm mb-2">
                                <option value="add">➕ Tambah Saldo</option>
                                <option value="subtract">➖ Kurangi Saldo</option>
                            </select>
                            <input type="number" v-model.number="adjustBalanceAmount" class="form-control form-control-sm mb-2" placeholder="Nominal (Rp)" min="1" />
                            <input type="text" v-model="adjustBalanceNote" class="form-control form-control-sm mb-2" placeholder="Catatan (opsional)" maxlength="200" />
                            <button @click="adminAdjustBalance" :disabled="!adjustBalanceAmount || adjustBalanceSubmitting" class="btn btn-sm btn-success btn-block">
                                <span v-if="adjustBalanceSubmitting"><i class="fas fa-spinner fa-spin"></i></span>
                                <span v-else><i class="fas fa-check"></i> Konfirmasi</span>
                            </button>
                        </div>
                        <button v-if="userApiBlocked == 1" @click="adminToggleBlock(false)" :disabled="blockSubmitting" class="btn btn-sm btn-success btn-block mt-2">
                            <span v-if="blockSubmitting"><i class="fas fa-spinner fa-spin"></i></span>
                            <span v-else><i class="fas fa-unlock"></i> Buka Blokir User</span>
                        </button>
                        <button v-else @click="adminToggleBlock(true)" :disabled="blockSubmitting" class="btn btn-sm btn-outline-danger btn-block mt-2">
                            <span v-if="blockSubmitting"><i class="fas fa-spinner fa-spin"></i></span>
                            <span v-else><i class="fas fa-ban"></i> Blokir User (Login + API)</span>
                        </button>
                    </div>

                    <div class="card p-3 mb-3">
                        <div class="d-flex justify-content-between align-items-center">
                            <h6 class="mb-0"><i class="fas fa-receipt text-info"></i> Riwayat Invoice User</h6>
                            <button @click="toggleInvoiceHistory" class="btn btn-sm btn-outline-info">
                                <i class="fas" :class="showInvoiceHistory ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                                {{ showInvoiceHistory ? 'Tutup' : 'Buka' }}
                            </button>
                        </div>
                        <div v-if="showInvoiceHistory" class="mt-3">
                            <div class="mb-2">
                                <select v-model="invoiceHistoryStatus" @change="loadInvoiceHistory" class="form-control form-control-sm">
                                    <option value="">Semua Status</option>
                                    <option value="success">Success (Lunas/Paid)</option>
                                    <option value="pending">Pending</option>
                                    <option value="refunded">Refunded</option>
                                    <option value="cancelled">Cancelled</option>
                                    <option value="failed">Failed</option>
                                </select>
                            </div>
                            <div v-if="invoiceHistoryLoading" class="text-center p-2">
                                <div class="spinner-border spinner-border-sm"></div> Memuat...
                            </div>
                            <div v-else>
                                <div v-if="invoiceHistorySummary.count_paid > 0" class="alert alert-success p-2 small mb-2">
                                    <strong><i class="fas fa-check-circle"></i> Total Paid: {{ formatSaldo(invoiceHistorySummary.total_paid) }}</strong>
                                    <span class="text-muted">({{ invoiceHistorySummary.count_paid }} transaksi)</span>
                                </div>
                                <div v-if="invoiceHistory.length === 0" class="text-muted small text-center p-2">
                                    <i class="fas fa-inbox"></i> Tidak ada transaksi
                                </div>
                                <div v-else class="invoice-list" style="max-height:400px;overflow-y:auto;">
                                    <div v-for="tx in invoiceHistory" :key="tx.id" class="invoice-item p-2 mb-2" style="background:#f8f9fa;border-radius:6px;border-left:3px solid;" :style="{ borderLeftColor: invoiceStatusColor(tx.status) }">
                                        <div class="d-flex justify-content-between">
                                            <span class="small font-weight-bold">{{ tx.transaction_id || ('TX-' + tx.id) }}</span>
                                            <span class="badge" :class="invoiceStatusBadge(tx.status)">{{ tx.status }}</span>
                                        </div>
                                        <div class="small text-muted">
                                            <i class="fas fa-tag"></i> {{ tx.mode || '-' }}
                                            <span v-if="tx.method"> · <i class="fas fa-credit-card"></i> {{ tx.method }}</span>
                                        </div>
                                        <div class="small">
                                            <strong class="text-success">{{ formatSaldo(tx.nominal) }}</strong>
                                            <span v-if="tx.coupon_used" class="text-warning ml-1"><i class="fas fa-ticket-alt"></i> {{ tx.coupon_used }}</span>
                                        </div>
                                        <div class="small text-muted">
                                            <i class="fas fa-clock"></i> {{ formatDate(tx.created_at) }}
                                        </div>
                                        <div v-if="tx.checkout_url" class="small mt-1">
                                            <a :href="tx.checkout_url" target="_blank" class="text-info"><i class="fas fa-external-link-alt"></i> Checkout URL</a>
                                        </div>
                                        <div v-if="tx.status === 'success'" class="mt-2">
                                            <button @click="sendPaidInvoice(tx)" :disabled="sendingPaidInvoiceId === tx.id" class="btn btn-sm btn-success btn-block">
                                                <span v-if="sendingPaidInvoiceId === tx.id"><i class="fas fa-spinner fa-spin"></i> Mengirim...</span>
                                                <span v-else><i class="fas fa-paper-plane"></i> Kirim Bukti PAID ke Tiket</span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="card p-3">
                        <h6>Detail Tiket</h6>
                        <table class="table table-sm small mb-0">
                            <tbody>
                                <tr><td class="text-muted">Number</td><td>{{ ticket.ticket_number }}</td></tr>
                                <tr><td class="text-muted">Status</td><td><span class="status-badge" :class="'status-' + ticket.status">{{ statusLabel(ticket.status) }}</span></td></tr>
                                <tr><td class="text-muted">Kategori</td><td>{{ categoryLabel(ticket.category) }}</td></tr>
                                <tr><td class="text-muted">Priority</td><td>{{ priorityLabel(ticket.priority) }}</td></tr>
                                <tr><td class="text-muted">Dibuat</td><td>{{ formatDate(ticket.created_at) }}</td></tr>
                                <tr v-if="ticket.last_user_reply_at"><td class="text-muted">Reply user</td><td>{{ formatRelative(ticket.last_user_reply_at) }}</td></tr>
                                <tr v-if="ticket.last_admin_reply_at"><td class="text-muted">Reply admin</td><td>{{ formatRelative(ticket.last_admin_reply_at) }}</td></tr>
                                <tr v-if="ticket.closed_at"><td class="text-muted">Ditutup</td><td>{{ formatDate(ticket.closed_at) }}</td></tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import moment from 'moment';
export default {
    data() {
        return {
            loading: false,
            sending: false,
            ticket: null,
            messages: [],
            user: null,
            userPackage: '',
            userPackageExpiredAt: 0,
            userApiBlocked: 0,
            userApiBlockedReason: '',
            agencyInfo: null,
            userTotalTickets: 0,
            replyText: '',
            replyFiles: [],
            showInvoice: false,
            invoiceLoading: false,
            invoicePackages: [],
            invoiceCurrentPackageId: 0,
            showExtendPackage: false,
            extendPackageDays: 30,
            extendPackageSubmitting: false,
            extendPackageOptions: [
                { days: 30, label: 'Bulanan' },
                { days: 183, label: '6 Bulan' },
                { days: 365, label: 'Tahunan' },
            ],
            showChangePackage: false,
            changePackageId: 0,
            changePackageDays: null,
            changePackageSubmitting: false,
            showAdjustBalance: false,
            adjustBalanceType: 'add',
            adjustBalanceAmount: null,
            adjustBalanceNote: '',
            adjustBalanceSubmitting: false,
            blockSubmitting: false,
            showInvoiceHistory: false,
            invoiceHistoryLoading: false,
            invoiceHistory: [],
            invoiceHistoryStatus: 'success',
            invoiceHistorySummary: { total_paid: 0, count_paid: 0 },
            sendingPaidInvoiceId: null,
            invoicePackageId: 0,
            invoiceUser: null,
            invoiceBank: null,
            invoiceText: '',
            invoiceSending: false,
            invoiceMarkPaid: false,
            aiDraftStarting: false,
            aiDraftProcessing: false,
            aiDraftReady: false,
            aiDraftId: '',
            aiDraftAdminSummary: '',
            aiDraftEngineeringPrompt: '',
            aiDraftNeedsEngineering: false,
            aiDraftSending: false,
            showAiAdminSummary: false,
            showAiEngineering: false,
            aiDraftPollTimer: null,
            aiDraftPollTicks: 0,
            aiDraftPollMaxTicks: 150,
        };
    },
    computed: {
        invoicePackagesMain() { return this.invoicePackages.filter(p => !p.is_addon); },
        invoicePackagesAddon() { return this.invoicePackages.filter(p => p.is_addon); },
        showAgencyRow() {
            return this.user && (this.user.level === 'user-agency' || this.user.level === 'cs');
        },
        expiredClass() {
            if (!this.userPackageExpiredAt) return 'text-muted';
            const days = moment(this.userPackageExpiredAt).diff(moment(), 'days');
            if (days < 0) return 'text-danger font-weight-bold';
            if (days <= 7) return 'text-warning font-weight-bold';
            return 'text-success';
        }
    },
    watch: {
        invoiceMarkPaid() {
            // Re-generate copywriting saat toggle PAID/Unpaid berubah
            if (this.invoicePackageId) this.generateCopywriting();
        }
    },
    mounted() { this.loadDetail(); },
    beforeDestroy() { this.stopAIDraftPoll(); },
    methods: {
        stopAIDraftPoll() {
            if (this.aiDraftPollTimer) {
                clearInterval(this.aiDraftPollTimer);
                this.aiDraftPollTimer = null;
            }
        },
        resetAIDraftUI() {
            this.aiDraftProcessing = false;
            this.aiDraftReady = false;
            this.aiDraftId = '';
            this.aiDraftAdminSummary = '';
            this.aiDraftEngineeringPrompt = '';
            this.aiDraftNeedsEngineering = false;
            this.showAiAdminSummary = false;
            this.showAiEngineering = false;
        },
        applyAIDraftStatus(d) {
            const st = (d && d.status) || 'idle';
            if (st === 'ready') {
                const becomingReady = !this.aiDraftReady;
                this.aiDraftProcessing = false;
                this.aiDraftReady = true;
                this.aiDraftId = d.draft_id || '';
                this.aiDraftAdminSummary = d.admin_summary || '';
                this.aiDraftEngineeringPrompt = d.engineering_prompt || '';
                this.aiDraftNeedsEngineering = !!d.needs_engineering;
                if (d.reply_text && becomingReady) {
                    this.replyText = d.reply_text;
                }
                this.stopAIDraftPoll();
                return;
            }
            if (st === 'processing') {
                this.aiDraftProcessing = true;
                this.aiDraftReady = false;
                return;
            }
            if (this.aiDraftProcessing && st === 'idle') {
                const msg = (d && d.error) || 'AI draft gagal atau tidak muncul di server. Silakan coba AI Draft lagi.';
                this.$toast.error(msg);
                this.resetAIDraftUI();
                this.stopAIDraftPoll();
            }
        },
        async pollAIDraftStatusOnce() {
            if (!this.ticket || !this.ticket.id) return;
            try {
                const r = await this.$store.dispatch('admin_support_ticket_ai_draft_status', this.ticket.id);
                const d = (r.data && r.data.data) || {};
                this.applyAIDraftStatus(d);
            } catch (e) {
                if (this.aiDraftProcessing) {
                    const msg = (e.response && e.response.data && e.response.data.message) || 'Gagal cek status AI draft';
                    this.$toast.error(msg);
                    this.resetAIDraftUI();
                    this.stopAIDraftPoll();
                }
            }
        },
        startAIDraftPoll() {
            this.stopAIDraftPoll();
            this.aiDraftPollTicks = 0;
            this.aiDraftPollTimer = setInterval(async () => {
                this.aiDraftPollTicks += 1;
                if (this.aiDraftPollTicks > this.aiDraftPollMaxTicks) {
                    this.$toast.error('AI draft belum selesai setelah ~10 menit. Coba lagi atau cek log server.');
                    this.resetAIDraftUI();
                    this.stopAIDraftPoll();
                    return;
                }
                await this.pollAIDraftStatusOnce();
            }, 4000);
        },
        async startAIDraft() {
            if (!this.ticket || this.ticket.status === 'closed') return;
            if (!confirm('Jalankan AI Draft untuk tiket #' + this.ticket.ticket_number + '? Investigasi + draft balasan (1–3 menit).')) return;
            this.aiDraftStarting = true;
            this.resetAIDraftUI();
            try {
                const r = await this.$store.dispatch('admin_support_ticket_ai_draft_start', this.ticket.id);
                const d = (r.data && r.data.data) || {};
                this.aiDraftProcessing = true;
                if (d.status === 'ready') {
                    await this.pollAIDraftStatusOnce();
                } else {
                    this.startAIDraftPoll();
                    await this.pollAIDraftStatusOnce();
                }
            } catch (err) {
                const msg = (err.response && err.response.data && err.response.data.message) || 'Gagal memulai AI draft';
                this.$toast.error(msg);
                if (err.response && err.response.status === 409) {
                    await this.pollAIDraftStatusOnce();
                    if (!this.aiDraftReady) this.startAIDraftPoll();
                }
            } finally {
                this.aiDraftStarting = false;
            }
        },
        async sendAIDraft() {
            if (!this.aiDraftId || !this.replyText.trim()) return;
            if (!confirm('Kirim balasan (AI draft) ke user untuk tiket #' + this.ticket.ticket_number + '?')) return;
            this.aiDraftSending = true;
            try {
                const r = await this.$store.dispatch('admin_support_ticket_ai_draft_send', {
                    id: this.ticket.id,
                    draftId: this.aiDraftId,
                    message: this.replyText.trim(),
                });
                if (r.data && r.data.data && r.data.data.message) {
                    this.messages.push(r.data.data.message);
                }
                this.replyText = '';
                this.resetAIDraftUI();
                this.$toast.success(r.data.message || 'Balasan AI terkirim');
                this.loadDetail();
            } catch (err) {
                this.$toast.error((err.response && err.response.data && err.response.data.message) || 'Gagal kirim draft AI');
            } finally {
                this.aiDraftSending = false;
            }
        },
        async discardAIDraft() {
            if (!confirm('Buang draft AI untuk tiket ini?')) return;
            this.aiDraftSending = true;
            try {
                await this.$store.dispatch('admin_support_ticket_ai_draft_discard', this.ticket.id);
                this.resetAIDraftUI();
                this.stopAIDraftPoll();
                this.$toast.success('Draft AI dibuang');
            } catch (err) {
                this.$toast.error((err.response && err.response.data && err.response.data.message) || 'Gagal buang draft');
            } finally {
                this.aiDraftSending = false;
            }
        },
        async toggleInvoice() {
            this.showInvoice = !this.showInvoice;
            if (this.showInvoice && this.invoicePackages.length === 0) {
                await this.loadInvoiceOptions();
            }
        },
        async ensurePackagesLoaded() {
            if (this.invoicePackages.length === 0) {
                await this.loadInvoiceOptions();
            }
        },
        async toggleChangePackage() {
            this.showChangePackage = !this.showChangePackage;
            if (this.showChangePackage) {
                await this.ensurePackagesLoaded();
            }
        },
        toggleExtendPackage() {
            this.showExtendPackage = !this.showExtendPackage;
            if (this.showExtendPackage && !this.extendPackageDays) {
                this.extendPackageDays = 30;
            }
        },
        extendPackageLabel(days) {
            const opt = (this.extendPackageOptions || []).find(o => o.days === days);
            return opt ? opt.label : (days + ' hari');
        },
        async adminExtendPackage() {
            const d = parseInt(this.extendPackageDays);
            if (isNaN(d) || d < 1) {
                this.$toast.error('Pilih durasi perpanjangan');
                return;
            }
            const label = this.extendPackageLabel(d);
            if (!confirm('Yakin perpanjang paket user ' + (this.user && this.user.email ? this.user.email : '-') + ' selama ' + label + ' (' + d + ' hari)?')) return;
            this.extendPackageSubmitting = true;
            try {
                const r = await this.$store.dispatch('admin_support_ticket_extend_package', { id: this.ticket.id, days: d });
                this.$toast.success(r.data.message || 'Berhasil perpanjang paket');
                this.showExtendPackage = false;
                this.loadDetail();
            } catch (err) {
                this.$toast.error((err.response && err.response.data && err.response.data.message) || 'Gagal perpanjang paket');
            } finally {
                this.extendPackageSubmitting = false;
            }
        },
        async adminChangePackage() {
            if (!this.changePackageId) return;
            const pkg = (this.invoicePackages || []).find(p => p.id === this.changePackageId);
            const pkgName = pkg ? pkg.name : 'paket terpilih';
            if (!confirm('Yakin ganti paket user ' + (this.user && this.user.email ? this.user.email : '-') + ' ke "' + pkgName + '"?')) return;
            this.changePackageSubmitting = true;
            try {
                const r = await this.$store.dispatch('admin_support_ticket_change_package', {
                    id: this.ticket.id,
                    package_id: this.changePackageId,
                    extend_days: this.changePackageDays || 0
                });
                this.$toast.success(r.data.message || 'Berhasil ganti paket');
                this.showChangePackage = false;
                this.changePackageId = 0;
                this.changePackageDays = null;
                this.loadDetail();
            } catch (err) {
                this.$toast.error((err.response && err.response.data && err.response.data.message) || 'Gagal ganti paket');
            } finally {
                this.changePackageSubmitting = false;
            }
        },
        async toggleInvoiceHistory() {
            this.showInvoiceHistory = !this.showInvoiceHistory;
            if (this.showInvoiceHistory) {
                await this.loadInvoiceHistory();
            }
        },
        async loadInvoiceHistory() {
            this.invoiceHistoryLoading = true;
            try {
                const r = await this.$store.dispatch('admin_support_invoice_history', {
                    id: this.ticket.id,
                    status: this.invoiceHistoryStatus,
                    limit: 50
                });
                const d = (r.data && r.data.data) || {};
                this.invoiceHistory = d.transactions || [];
                this.invoiceHistorySummary = {
                    total_paid: d.total_paid || 0,
                    count_paid: d.count_paid || 0
                };
            } catch (err) {
                this.$toast.error('Gagal memuat riwayat invoice');
            } finally {
                this.invoiceHistoryLoading = false;
            }
        },
        async sendPaidInvoice(tx) {
            const customMsg = prompt('Pesan tambahan untuk user (kosongkan = pakai template default):', '');
            if (customMsg === null) return; // user cancel
            if (!confirm('Yakin kirim bukti invoice PAID #' + (tx.transaction_id || tx.id) + ' (' + this.formatSaldo(tx.nominal) + ') ke tiket ini?')) return;
            this.sendingPaidInvoiceId = tx.id;
            try {
                const r = await this.$store.dispatch('admin_support_send_paid_invoice', {
                    id: this.ticket.id,
                    transaction_id: tx.id,
                    message: customMsg || ''
                });
                this.$toast.success(r.data.message || 'Bukti invoice PAID berhasil dikirim');
                this.loadDetail(); // refresh thread
            } catch (err) {
                this.$toast.error((err.response && err.response.data && err.response.data.message) || 'Gagal kirim bukti invoice');
            } finally {
                this.sendingPaidInvoiceId = null;
            }
        },
        invoiceStatusColor(status) {
            const map = { success: '#28a745', paid: '#28a745', pending: '#ffc107', refunded: '#17a2b8', cancelled: '#6c757d', failed: '#dc3545' };
            return map[status] || '#6c757d';
        },
        invoiceStatusBadge(status) {
            const map = { success: 'badge-success', paid: 'badge-success', pending: 'badge-warning', refunded: 'badge-info', cancelled: 'badge-secondary', failed: 'badge-danger' };
            return map[status] || 'badge-secondary';
        },
        async adminResetPassword() {
            const newPwd = prompt('Password baru untuk user ' + (this.user && this.user.email ? this.user.email : '-') + ' (min 6 karakter):', '');
            if (!newPwd) return;
            if (newPwd.length < 6) {
                this.$toast.error('Password minimal 6 karakter');
                return;
            }
            if (!confirm('Yakin reset password user ' + (this.user && this.user.email ? this.user.email : '-') + ' ke "' + newPwd + '"?\n\nUser akan perlu login ulang dengan password baru.')) return;
            try {
                const r = await this.$store.dispatch('admin_support_ticket_reset_password', { id: this.ticket.id, new_password: newPwd });
                this.$toast.success(r.data.message || 'Password berhasil direset');
            } catch (err) {
                this.$toast.error((err.response && err.response.data && err.response.data.message) || 'Gagal reset password');
            }
        },

        async adminLoginAsUser() {
            const email = (this.user && this.user.email) ? this.user.email : '-';
            if (!this.user || !this.user.id) { this.$toast.error('Data user tidak tersedia'); return; }
            if (!confirm('Login sebagai user ' + email + '? Anda akan masuk ke akun user ini. Untuk kembali ke admin, klik tombol Kembali ke Admin yang muncul di layar.')) return;
            try {
                localStorage.setItem('admin_return_url', this.$route.fullPath);
                await this.$store.dispatch('auth_login_admin', { id: this.user.id });
                await this.$root.$emit('login', true);
                await this.$router.push('/dashboard');
                window.location.reload();
            } catch (err) {
                this.$toast.error((err.response && err.response.data && err.response.data.message) || 'Gagal login sebagai user');
            }
        },
        toggleAdjustBalance() {
            this.showAdjustBalance = !this.showAdjustBalance;
        },
        async adminAdjustBalance() {
            const amt = parseInt(this.adjustBalanceAmount);
            if (isNaN(amt) || amt <= 0) {
                this.$toast.error('Nominal harus angka lebih dari 0');
                return;
            }
            const label = this.adjustBalanceType === 'add' ? 'MENAMBAH' : 'MENGURANGI';
            const email = (this.user && this.user.email) ? this.user.email : '-';
            if (!confirm('Yakin ' + label + ' saldo user ' + email + ' sebesar ' + this.formatSaldo(amt) + '?')) return;
            this.adjustBalanceSubmitting = true;
            try {
                const r = await this.$store.dispatch('admin_support_ticket_adjust_balance', {
                    id: this.ticket.id,
                    type: this.adjustBalanceType,
                    amount: amt,
                    note: this.adjustBalanceNote || ''
                });
                const d = (r.data && r.data.data) || {};
                this.$toast.success('Saldo diperbarui: ' + this.formatSaldo(d.old_balance || 0) + ' → ' + this.formatSaldo(d.new_balance || 0));
                this.showAdjustBalance = false;
                this.adjustBalanceAmount = null;
                this.adjustBalanceNote = '';
                this.adjustBalanceType = 'add';
                this.loadDetail();
            } catch (err) {
                this.$toast.error((err.response && err.response.data && err.response.data.message) || 'Gagal update saldo');
            } finally {
                this.adjustBalanceSubmitting = false;
            }
        },
        async adminToggleBlock(block) {
            const email = (this.user && this.user.email) ? this.user.email : '-';
            let reason = '';
            if (block) {
                reason = prompt('Alasan blokir user ' + email + '?\n(user TIDAK bisa login & TIDAK bisa pakai API StarSender)', '');
                if (reason === null) return; // batal
                if (!confirm('Yakin BLOKIR user ' + email + '?\n\nUser tidak akan bisa LOGIN & akses API-nya diblokir.')) return;
            } else {
                if (!confirm('Buka blokir user ' + email + '?\nUser bisa login & pakai API lagi.')) return;
            }
            this.blockSubmitting = true;
            try {
                const r = await this.$store.dispatch('admin_support_ticket_toggle_block', { id: this.ticket.id, block: block, reason: reason || '' });
                this.$toast.success(r.data.message || (block ? 'User berhasil diblokir' : 'Blokir user dibuka'));
                this.loadDetail();
            } catch (err) {
                this.$toast.error((err.response && err.response.data && err.response.data.message) || 'Gagal memperbarui status blokir');
            } finally {
                this.blockSubmitting = false;
            }
        },
        async loadInvoiceOptions() {
            this.invoiceLoading = true;
            try {
                const r = await this.$store.dispatch('admin_support_invoice_options', this.ticket.id);
                const d = (r.data && r.data.data) || {};
                this.invoicePackages = d.packages || [];
                this.invoiceCurrentPackageId = d.current_package_id || 0;
                this.invoiceUser = d.user || null;
                this.invoiceBank = d.bank || null;
                this.invoicePackageId = this.invoiceCurrentPackageId || (this.invoicePackages[0] && this.invoicePackages[0].id) || 0;
                this.generateCopywriting();
            } catch (e) {
                this.$toast.error('Gagal load data invoice');
            }
            this.invoiceLoading = false;
        },
        generateCopywriting() {
            const p = this.invoicePackages.find(x => x.id === this.invoicePackageId);
            if (!p) { this.invoiceText = ''; return; }
            const nm = (this.invoiceUser && this.invoiceUser.name) || 'Kak';
            const isRenew = this.invoicePackageId === this.invoiceCurrentPackageId;
            const action = isRenew ? 'perpanjangan' : 'ganti';
            const durasi = p.duration ? (p.duration + ' hari') : '-';
            if (this.invoiceMarkPaid) {
                // Copywriting untuk Invoice PAID (sudah lunas)
                this.invoiceText =
                    'Halo ' + nm + ' \ud83d\ude0a\n\n' +
                    'Terima kasih banyak Kakak sudah melakukan pembayaran \ud83d\ude4f\n\n' +
                    'Berikut kami kirimkan bukti invoice PAID (Lunas) untuk transaksi Kakak di StarSender:\n\n' +
                    '\ud83e\uddfe Paket: ' + p.name + '\n' +
                    '\u23f3 Durasi: ' + durasi + '\n' +
                    '\ud83d\udcb0 Total: ' + this.formatSaldo(p.price) + '\n' +
                    '\u2705 Status: PAID (LUNAS)\n\n' +
                    'Paket Kakak sudah aktif dan siap digunakan \ud83c\udf89\n\n' +
                    '\ud83d\udcce Bukti invoice PAID terlampir di file PDF di bawah ini.\n' +
                    'Mohon disimpan sebagai bukti pembayaran ya Kak.\n\n' +
                    'Terima kasih atas kepercayaannya kepada StarSender \ud83c\udf1f\n\n' +
                    'Salam,\n' +
                    'Tim Support StarSender';
            } else {
                // Copywriting untuk Invoice UNPAID (tagihan, menunggu pembayaran)
                this.invoiceText =
                    'Halo ' + nm + ' \ud83d\udc4b\n\n' +
                    'Berikut kami kirimkan invoice untuk ' + action + ' paket di StarSender:\n\n' +
                    '\ud83e\uddfe Paket: ' + p.name + '\n' +
                    '\u23f3 Durasi: ' + durasi + '\n' +
                    '\ud83d\udcb0 Total: ' + this.formatSaldo(p.price) + '\n\n' +
                    '\ud83d\udcb3 Pembayaran transfer ke salah satu rekening:\n' +
                    '\u2022 BCA 7370673556 a/n Yohanes Santoso\n' +
                    '\u2022 Mandiri 1230005692423 a/n Yohanes Santoso\n\n' +
                    'Setelah transfer, mohon kirim foto bukti pembayaran ke tiket ini ya \ud83d\ude4f\n' +
                    'Paket akan kami proses maksimal 1x24 jam setelah pembayaran dikonfirmasi.\n\n' +
                    '\ud83d\udcce Detail lengkap ada di file PDF invoice terlampir di bawah.\n\n' +
                    'Terima kasih \ud83c\udf1f\n' +
                    'Tim Support StarSender';
            }
        },
        async sendInvoice() {
            if (!this.invoicePackageId) { this.$toast.error('Pilih paket dulu'); return; }
            const p = this.invoicePackages.find(x => x.id === this.invoicePackageId);
            if (!confirm('Kirim invoice paket "' + (p ? p.name : '') + '" ke tiket #' + this.ticket.ticket_number + '?')) return;
            this.invoiceSending = true;
            try {
                const r = await this.$store.dispatch('admin_support_send_invoice', {
                    id: this.ticket.id,
                    package_id: this.invoicePackageId,
                    message: this.invoiceText.trim(),
                    mark_paid: this.invoiceMarkPaid,
                });
                if (r.data && r.data.data && r.data.data.message) this.messages.push(r.data.data.message);
                this.$toast.success('Invoice terkirim ke tiket + email user');
                this.showInvoice = false;
                this.loadDetail();
            } catch (e) {
                this.$toast.error((e.response && e.response.data && e.response.data.message) || 'Gagal kirim invoice');
            }
            this.invoiceSending = false;
        },
        formatSaldo(v) {
            const n = Number(v) || 0;
            return 'Rp ' + n.toLocaleString('id-ID');
        },
        async loadDetail() {
            this.loading = true;
            try {
                const id = this.$route.params.id;
                const r = await this.$store.dispatch('admin_support_ticket_detail', id);
                this.ticket = r.data.data.ticket;
                this.messages = r.data.data.messages || [];
                this.user = r.data.data.user;
                this.userPackage = r.data.data.user_package || '';
                this.userPackageExpiredAt = r.data.data.user_package_expired_at || 0;
                this.userApiBlocked = r.data.data.user_api_blocked || 0;
                this.userApiBlockedReason = r.data.data.user_api_blocked_reason || '';
                this.agencyInfo = r.data.data.agency_info || null;
                this.userTotalTickets = r.data.data.user_total_tickets || 0;
                await this.pollAIDraftStatusOnce();
                if (this.aiDraftReady) {
                    /* draft pending from worker — show green box on refresh */
                } else if (this.aiDraftProcessing && !this.aiDraftPollTimer) {
                    this.startAIDraftPoll();
                }
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Gagal load tiket');
            }
            this.loading = false;
        },
        async updateStatus() {
            try {
                await this.$store.dispatch('admin_support_ticket_status', { id: this.ticket.id, status: this.ticket.status });
                this.$toast.success('Status updated');
                this.loadDetail();
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Gagal update status');
                this.loadDetail();
            }
        },
        onFileSelect(e) {
            const files = Array.from(e.target.files || []);
            for (const f of files) {
                if (f.size > 25 * 1024 * 1024) {
                    this.$toast.error(`File "${f.name}" melebihi 25MB`);
                    continue;
                }
                if (this.replyFiles.length >= 5) {
                    this.$toast.error('Maksimal 5 file');
                    break;
                }
                this.replyFiles.push(f);
            }
            e.target.value = '';
        },
        removeFile(i) { this.replyFiles.splice(i, 1); },
        async sendReply() {
            if (!this.replyText.trim()) return;
            if (!confirm(`Balas tiket #${this.ticket.ticket_number} dari ${(this.user && this.user.name) || (this.user && this.user.email) || 'user'}?`)) return;
            this.sending = true;
            try {
                const fd = new FormData();
                fd.append('message', this.replyText.trim());
                this.replyFiles.forEach(f => fd.append('files', f));
                const r = await this.$store.dispatch('admin_support_ticket_reply', { id: this.ticket.id, formData: fd });
                this.messages.push(r.data.data.message);
                this.replyText = '';
                this.replyFiles = [];
                this.$toast.success('Balasan terkirim + email forward ke user');
                this.loadDetail();
            } catch (e) {
                this.$toast.error(e.response?.data?.message || 'Gagal kirim balasan');
            }
            this.sending = false;
        },
        senderLabel(t) { return { user: (this.user && this.user.name) || 'User', admin: 'Tim Support', system: 'Sistem' }[t] || t; },
        statusLabel(s) { return { open: 'Open', in_progress: 'Diproses', waiting_user: 'Menunggu User', closed: 'Closed', refund: 'Refund' }[s] || s; },
        categoryLabel(c) { return { general: 'Umum', bug: 'Bug', billing: 'Pembayaran', feature: 'Fitur', other: 'Lainnya' }[c] || c; },
        priorityLabel(p) { return { low: 'Rendah', normal: 'Normal', high: 'Tinggi', urgent: 'Urgent' }[p] || p; },
        formatExpired() {
            if (!this.userPackageExpiredAt) return '-';
            const exp = moment(this.userPackageExpiredAt);
            const now = moment();
            const days = exp.diff(now, 'days');
            const dateStr = exp.format('DD MMM YYYY');
            if (days < 0) return dateStr + ' (Expired ' + Math.abs(days) + ' hari)';
            if (days <= 7) return dateStr + ' (' + days + ' hari lagi)';
            return dateStr;
        },
        formatDate(ts) { return ts ? moment(ts).format('DD MMM YYYY HH:mm') : '-'; },
        formatRelative(ts) { return ts ? moment(ts).fromNow() : ''; },
        formatMessage(m) {
            const escaped = (m || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
            const linked = escaped.replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" target="_blank" rel="noopener">$1</a>');
            return linked.replace(/\n/g, '<br>');
        },
        parseAttachments(raw) {
            if (!raw) return [];
            try { if (typeof raw === 'string') return JSON.parse(raw); return raw; } catch { return []; }
        },
        isImage(mime) { return (mime || '').startsWith('image/'); },
        isVideo(mime) { return (mime || '').startsWith('video/'); },
        fileIcon(mime) {
            mime = mime || '';
            if (mime.includes('pdf')) return 'far fa-file-pdf';
            if (mime.includes('word')) return 'far fa-file-word';
            if (mime.includes('excel') || mime.includes('sheet')) return 'far fa-file-excel';
            if (mime.includes('zip')) return 'far fa-file-archive';
            if (mime.startsWith('audio/')) return 'far fa-file-audio';
            return 'far fa-file';
        },
        formatSize(n) {
            if (!n) return '';
            if (n < 1024) return n + ' B';
            if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
            return (n / (1024 * 1024)).toFixed(1) + ' MB';
        },
        openLightbox(url) { window.open(url, '_blank'); },
    },
};
</script>

<style scoped>
.thread-card { max-height: 600px; overflow-y: auto; }
.message-row { display: flex; margin-bottom: 14px; }
.sender-user { justify-content: flex-start; }
.sender-admin { justify-content: flex-end; }
.sender-system { justify-content: center; }
.message-bubble {
    max-width: 75%;
    padding: 10px 14px;
    border-radius: 12px;
    font-size: 14px;
    line-height: 1.5;
}
.bubble-user { background: #f3f4f6; color: #1f2937; }
.bubble-admin { background: #1976d2; color: #fff; }
.bubble-system {
    background: #fef3c7;
    color: #78350f;
    font-style: italic;
    font-size: 12px;
    text-align: center;
}
.bubble-admin .text-muted { color: rgba(255,255,255,0.8) !important; }
.bubble-admin a { color: #fff; text-decoration: underline; }
.attach-image { max-width: 240px; max-height: 180px; border-radius: 8px; cursor: pointer; display: block; }
.attach-video { max-width: 320px; max-height: 240px; border-radius: 8px; display: block; }
.attach-file {
    display: inline-block;
    background: rgba(0, 0, 0, 0.05);
    padding: 6px 10px;
    border-radius: 6px;
    color: inherit;
    text-decoration: none;
    font-size: 13px;
}
.bubble-admin .attach-file { background: rgba(255,255,255,0.15); color: #fff; }
.message-attachments { margin-top: 8px; }
.attachment-item { margin-bottom: 8px; }
.status-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
}
.status-open { background: #fef3c7; color: #92400e; }
.status-in_progress { background: #dbeafe; color: #1e40af; }
.status-waiting_user { background: #fce7f3; color: #9d174d; }
.status-closed { background: #d1fae5; color: #065f46; }
.status-refund { background: #ede9fe; color: #5b21b6; }
</style>
